import { useCallback, useMemo } from 'react'

import { useLocation, useParams } from 'react-router-dom'

import { APP } from 'src/Configs/App'
import { rtkApi } from 'src/Services/rtkApi'
import {
  CampaignPlatformTypeEnum,
  CampaignStatusEnum,
  CampaignToSchedule,
  SpotifyCampaignAiUsageEnum,
  SpotifyCampaignMatchingType,
} from 'src/Types/campaigns'
import { ANY_LANGUAGE_ID, NO_VOCAL } from 'src/Constants/constants'
import { useAppDispatch } from 'src/Hooks/redux'
import { handleTakePlatformFromUrl } from 'src/Constants/functions'

interface PreReleaseTrackFileMetadata {
  createdAt: string
  format: string
  originalFileName: string
  size: number
}

export interface SpotifyCampaignGenre {
  id: number
  genreName: string
}

export interface SpotifyCampaignLanguage {
  id: number
  languageName: string
}

export interface SpotifyCampaignTrack {
  audioUrl: string | null
  authorName: string
  genres: Array<SpotifyCampaignGenre>
  languages: Array<SpotifyCampaignLanguage>
  spotifyId: string | null
  trackImage: string
  trackName: string
}

interface SpotifyCampaignMetadata {
  maxCuratorsCount: null | number
  maxPlaylistsCount: null | number
  minCuratorsCount: null | number
}

export type SpotifyCampaignType = 'basic' | 'first' | 'relaunched'

export interface SpotifyCampaignStatusResponse {
  matchingType: SpotifyCampaignMatchingType | null
  aiUsage: SpotifyCampaignAiUsageEnum | null
  amount: number
  endDate: string | null
  id: number
  isPreRelease: boolean
  releaseDate: string | null
  metadata: SpotifyCampaignMetadata
  playlistsAdds: number
  refund: number
  reviewedCount: number
  spent: number
  startDate: string | null
  status: CampaignStatusEnum
  trackActiveCampaignsCount: number
  type: SpotifyCampaignType
  userId: number
  track: SpotifyCampaignTrack
  trackNote: string | null
}

const spotifyCampaignStatusApi = rtkApi.injectEndpoints({
  endpoints: (builder) => ({
    getSpotifyCampaignStatus: builder.query<
      SpotifyCampaignStatusResponse,
      number
    >({
      query: (campaignId) =>
        `${APP.TIKTOK_SERVER}/artist/spotify-campaigns/${campaignId}`,
      providesTags: ['SpotifyCampaignDetails'],
    }),
    getPreReleaseTrackFileMetadata: builder.query<
      PreReleaseTrackFileMetadata,
      number
    >({
      query: (campaignId) =>
        `${APP.TIKTOK_SERVER}/artist/spotify-campaigns/${campaignId}/pre-release-track-file/metadata`,
      providesTags: ['SpotifyCampaignDetails'],
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetSpotifyCampaignStatusQuery,
  useLazyGetSpotifyCampaignStatusQuery,
  useGetPreReleaseTrackFileMetadataQuery,
  util: spotifyCampaignStatusApiUtil,
} = spotifyCampaignStatusApi

const mapToLegacyFormat = (
  data: SpotifyCampaignStatusResponse,
): CampaignToSchedule => ({
  id: data.id,
  status: data.status,
  type: data.type,
  genres: data.track.genres.map((g) => ({
    id: g.id,
    name: g.genreName,
    value: g.id,
  })),
  languages: data.track.languages.map((l) => ({
    id: l.id,
    name: l.id === NO_VOCAL ? 'Instrumental' : l.languageName,
    value: l.id,
  })),
  start_date: data.startDate || '',
  is_feed: data.matchingType === SpotifyCampaignMatchingType.FEED,
  absoluteCuratorsCount: 0,
  track: {
    title: data.track.trackName,
    author: data.track.authorName,
    cover: data.track.trackImage,
  },
  matchingType: data.matchingType,
  aiUsage: data.aiUsage,
  trackNote: data.trackNote,
})

export interface SpotifyCampaignTrackFile {
  url: string
  name: string
  sizeBytes: number | null
}

export interface UseSpotifyCampaignStatusResult {
  campaignDetails: CampaignToSchedule | null
  isPreRelease: boolean
  releaseDate: string | null
  trackFile: SpotifyCampaignTrackFile | null
  matchingType: SpotifyCampaignMatchingType | null
  isLoading: boolean
  isFetching: boolean
  isError: boolean
  genresList: string
  hasSelectedLanguagesWithoutAny: boolean
  setStartDate: (date: string) => void
  setMatchingType: (matchingType: SpotifyCampaignMatchingType) => void
}

export const useSpotifyCampaignStatus = (
  campaignId?: number,
): UseSpotifyCampaignStatusResult => {
  const dispatch = useAppDispatch()
  const params = useParams()
  const { pathname } = useLocation()
  const id = campaignId ?? Number(params.number || params.campaignID)

  const isTikTokCampaign =
    handleTakePlatformFromUrl(pathname) === CampaignPlatformTypeEnum.TIKTOK

  const { data, isLoading, isFetching, isError } =
    useGetSpotifyCampaignStatusQuery(id, {
      skip: !id || isTikTokCampaign,
    })
  const { data: trackFileMetadata } = useGetPreReleaseTrackFileMetadataQuery(
    id,
    { skip: !id || !data?.track.audioUrl || isTikTokCampaign },
  )

  const setStartDate = useCallback(
    (date: string) => {
      if (!id) {
        return
      }
      dispatch(
        spotifyCampaignStatusApiUtil.updateQueryData(
          'getSpotifyCampaignStatus',
          id,
          (draft) => {
            if (draft) {
              draft.startDate = date
            }
          },
        ),
      )
    },
    [dispatch, id],
  )

  const setMatchingType = useCallback(
    (matchingType: SpotifyCampaignMatchingType) => {
      if (!id) {
        return
      }
      dispatch(
        spotifyCampaignStatusApiUtil.updateQueryData(
          'getSpotifyCampaignStatus',
          id,
          (draft) => {
            draft.matchingType = matchingType
          },
        ),
      )
    },
    [dispatch, id],
  )

  const campaignDetails = useMemo(
    () => (data ? mapToLegacyFormat(data) : null),
    [data],
  )

  const trackFile = useMemo(() => {
    if (!data?.track.audioUrl) {
      return null
    }
    return {
      url: data.track.audioUrl,
      name: trackFileMetadata?.originalFileName ?? '',
      sizeBytes: trackFileMetadata?.size ?? null,
    }
  }, [data, trackFileMetadata])

  const genresList = useMemo(
    () => data?.track.genres.map((g) => g.genreName).join(', ') || '',
    [data],
  )

  const hasSelectedLanguagesWithoutAny = useMemo(
    () =>
      Boolean(
        data?.track.languages.filter((l) => l.id !== ANY_LANGUAGE_ID).length,
      ),
    [data],
  )

  return {
    matchingType: data?.matchingType ?? null,
    isPreRelease: Boolean(data?.isPreRelease),
    releaseDate: data?.releaseDate ?? null,
    campaignDetails,
    trackFile,
    isLoading,
    isFetching,
    isError,
    genresList,
    hasSelectedLanguagesWithoutAny,
    setStartDate,
    setMatchingType,
  }
}
